import { useCallback, useEffect, useState } from "react";
import { Col, Row } from "react-bootstrap";
import {
  assignGroupToReport,
  assignUserToReport,
  deleteGroupFromReport,
  deleteUserFromReport,
  listAssignedUsers,
  listGroupsByReport,
  listUsersWithoutSpecificReport,
  updateUserFromReport,
} from "../../../../apis";
import { useTranslation } from "react-i18next";
import SearchBar from "../../../SearchBar/Search";
import { MCButton, MCTooltip } from "../../../MainComponents";
import { enqueueSnackbar, useSnackbar } from "notistack";
import Icon from "../../../Icon/Icon";
import { ModalAssignUser } from "./ModalAssignUser";
import { ListItem, ListItemAvatar, ListItemText, Avatar, IconButton, Tooltip, useTheme } from "@mui/material";
import { Confirmation } from "../../../Modal/Confirmation/Confirmation";

import "./styles.scss";

const sessionEmail = JSON.parse(localStorage.getItem("Session"))?.email || "";

export const Users = ({ idReport }) => {
  const [t] = useTranslation("users");
  const theme = useTheme();

  const [activeUsers, setActiveUsers] = useState([]);
  const [inactiveUsers, setInactiveUsers] = useState([]);
  const [blockedUsers, setBlockedUsers] = useState([]);
  const [filteredInactiveUsers, setFilteredInactiveUsers] = useState([]);
  const [search, setSearch] = useState("");
  const [showModalAddExternalUser, setShowModalAddExternalUser] = useState(false);
  const [showAllInactive, setShowAllInactive] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [showConfirmResponsable, setShowConfirmResponsable] = useState(false);
  const [pendingResponsableUser, setPendingResponsableUser] = useState(null);

  // 🔹 Asignación por grupos
  const [groups, setGroups] = useState([]);
  const [groupsLoading, setGroupsLoading] = useState(true);
  const [busyGroupId, setBusyGroupId] = useState(null);
  const [showGroups, setShowGroups] = useState(false);

  // 🔹 Verificar si un usuario tiene el rol de responsable de investigación
  const hasInvestigationResponsable = (user) => {
    return user?.assign_details?.rolesInvestigation?.includes("investigation_responsable") || false;
  };

  // 🔹 Abrir modal de confirmación para asignar responsable
  const handleCrownClick = (user, e) => {
    e.stopPropagation(); // Evitar que se abra la modal de usuario

    const userHasRole = hasInvestigationResponsable(user);

    if (userHasRole) {
      // Si ya tiene el rol, quitarlo directamente (sin confirmación)
      handleRemoveResponsable(user);
    } else {
      // Mostrar modal de confirmación
      setPendingResponsableUser(user);
      setShowConfirmResponsable(true);
    }
  };

  // 🔹 Quitar el rol de responsable
  const handleRemoveResponsable = async (user) => {
    try {
      const newRolesInvestigation = (user.assign_details?.rolesInvestigation || [])
        .filter(role => role !== "investigation_responsable");

      await updateUserFromReport(
        user.id,
        {
          rolesInvestigation: newRolesInvestigation,
          rolesResolution: user.assign_details?.rolesResolution || [],
          details: user.assign_details?.details || ""
        },
        idReport
      );

      setActiveUsers(prev =>
        prev.map(u =>
          u.id === user.id
            ? { ...u, assign_details: { ...u.assign_details, rolesInvestigation: newRolesInvestigation } }
            : u
        )
      );
      enqueueSnackbar(t("User.responsable_removed"), { variant: "info" });
    } catch (error) {
      enqueueSnackbar(t("User.error_updating_responsable"), { variant: "error" });
      console.error(error);
    }
  };

  // 🔹 Confirmar asignación de responsable
  const handleConfirmResponsable = async () => {
    if (!pendingResponsableUser) return;

    const user = pendingResponsableUser;

    try {
      // Agregar el rol al usuario
      const newRolesInvestigation = [
        ...(user.assign_details?.rolesInvestigation || []),
        "investigation_responsable"
      ];

      await updateUserFromReport(
        user.id,
        {
          rolesInvestigation: newRolesInvestigation,
          rolesResolution: user.assign_details?.rolesResolution || [],
          details: user.assign_details?.details || ""
        },
        idReport
      );

      setActiveUsers(prev =>
        prev.map(u => {
          if (u.id === user.id) {
            return { ...u, assign_details: { ...u.assign_details, rolesInvestigation: newRolesInvestigation } };
          }
          return u;
        })
      );
      enqueueSnackbar(t("User.responsable_assigned"), { variant: "success" });
    } catch (error) {
      enqueueSnackbar(t("User.error_updating_responsable"), { variant: "error" });
      console.error(error);
    } finally {
      setShowConfirmResponsable(false);
      setPendingResponsableUser(null);
    }
  };

  // 🔹 Cargar usuarios activos e inactivos
  const loadUsers = useCallback(() => {
    setIsLoading(true);
    return Promise.all([
      listAssignedUsers(idReport),
      listUsersWithoutSpecificReport(idReport),
    ])
      .then(([assign, without]) => {
        setActiveUsers(assign.users);
        setInactiveUsers(without.users);
        setBlockedUsers(without.blocked_users || []);
      })
      .catch((err) => {
        console.log(err);
        setActiveUsers([]);
        setInactiveUsers([]);
        setBlockedUsers([]);
      })
      .finally(() => setIsLoading(false));
  }, [idReport]);

  useEffect(() => {
    loadUsers();
  }, [loadUsers]);

  // 🔹 Cargar grupos y su estado de asignación para este reporte
  const loadGroups = useCallback(() => {
    setGroupsLoading(true);
    return listGroupsByReport(idReport)
      .then((resp) => {
        const list = resp?.groups_list || {};
        setGroups(
          Object.entries(list)
            .map(([id, group]) => ({
              id: Number(id),
              name: group.name,
              users: Object.values(group.users || {}),
              assigned: Boolean(group.assigned_to_report),
              isActive: Boolean(group.is_active),
            }))
            // Solo ofrecemos grupos activos y con al menos un miembro
            .filter((group) => group.isActive && group.users.length > 0)
        );
      })
      .catch((err) => {
        console.error(err);
        setGroups([]);
      })
      .finally(() => setGroupsLoading(false));
  }, [idReport]);

  useEffect(() => {
    loadGroups();
  }, [loadGroups]);

  // 🔹 Asignar / quitar todos los usuarios de un grupo al reporte.
  // Una acción de grupo mueve gente entre las listas, así que recargamos ambas.
  const handleGroupAction = async (group, action) => {
    setBusyGroupId(group.id);
    try {
      if (action === "assign") {
        await assignGroupToReport(group.id, idReport);
        enqueueSnackbar(t("Groups.group_assigned"), { variant: "success" });
      } else {
        await deleteGroupFromReport(group.id, idReport);
        enqueueSnackbar(t("Groups.group_unassigned"), { variant: "info" });
      }
      await Promise.all([loadUsers(), loadGroups()]);
    } catch (error) {
      console.error(error);
      enqueueSnackbar(t("Groups.group_action_error"), { variant: "error" });
    } finally {
      setBusyGroupId(null);
    }
  };

  // 🔹 Actualizar asignación de usuarios
  const handleChange = async (user, setIsLoading, target, rolesInvestigation, rolesResolution, details) => {
    setIsLoading(true);
    try {
      if (target === "active") {
        await deleteUserFromReport(user.id, idReport);
        const kickedUser = activeUsers.find((item) => item.id === user.id);
        setActiveUsers(activeUsers.filter((item) => item.id !== user.id));
        setInactiveUsers([...inactiveUsers, kickedUser]);
        enqueueSnackbar("Usuario desactivado", { variant: "info" });
      }

      if (target === "inactive") {
        await assignUserToReport(user.id, idReport, rolesInvestigation, rolesResolution, details);
        const activatedUser = inactiveUsers.find((item) => item.id === user.id);
        setInactiveUsers(inactiveUsers.filter((item) => item.id !== user.id));
        setActiveUsers([...activeUsers, { ...activatedUser, assign_details: { rolesInvestigation, rolesResolution, details } }]);
        enqueueSnackbar("Usuario asignado correctamente", { variant: "success" });
      }

      if (target === "update") {
        await updateUserFromReport(user.id, { rolesInvestigation, rolesResolution, details }, idReport);
        setActiveUsers((prev) =>
          prev.map((item) =>
            item.id === user.id ? { ...item, assign_details: { rolesInvestigation, rolesResolution, details } } : item
          )
        );
        enqueueSnackbar("Usuario actualizado", { variant: "success" });
      }
    } catch (error) {
      enqueueSnackbar("Error al procesar la solicitud", { variant: "error" });
      console.error(error);
    } finally {
      setIsLoading(false);
    }
  };

  // 🔹 Filtro de búsqueda
  const handleSearch = (value) => {
    setSearch(value);
    if (value.trim() === "") {
      setFilteredInactiveUsers([]);
      return;
    }
    const cleaned = value.trim().toLowerCase();
    const filtered = inactiveUsers.filter((u) => u.name.toLowerCase().includes(cleaned));
    setFilteredInactiveUsers(filtered);
  };

  // 🔹 Iniciales para avatar
  const getInitials = (name = "") =>
    name
      .split(" ")
      .map((n) => n[0]?.toUpperCase())
      .join("")
      .slice(0, 2);

  // 🔹 Componente interno para cada usuario
  const UserListItem = ({ user, target }) => {
    const [showModal, setShowModal] = useState(false);
    const [isItemLoading, setIsItemLoading] = useState(false);
    const { enqueueSnackbar } = useSnackbar();

    const handleClick = () => {
      if (user.email === sessionEmail) return;
      setShowModal(true);
    };

    const getAvatarColor = (target) => {
      switch(target) {
        case "active":
          return "#02665c";
          case "inactive":
            return "#5c666e";
            case "blocked": 
            return "#bc342d";
            default:
              return "#EF9A9A";
      }
    }

    return (
      <>
        <ListItem
          button
          disabled={isItemLoading}
          onClick={handleClick}
          sx={{
            backgroundColor: user.email === sessionEmail ? "#e0f7fa" : "background.paper",
            borderRadius: "8px",
            mb: 1,
            border: "1px solid #ddd",
            cursor: "pointer",
          }}
        >
          <ListItemAvatar>
            <Avatar sx={{ bgcolor: getAvatarColor(target), width: 40, height: 40, fontSize: 22 }}>
              {getInitials(user.name)}
            </Avatar>
          </ListItemAvatar>

          <ListItemText
            primary={
              <>
                {user.name}
              </>
            }
            secondary={user.email}
          />

          {target === "active" && (
            <Tooltip title={t("User.investigation_responsable")} arrow>
              <IconButton
                onClick={(e) => handleCrownClick(user, e)}
                sx={{
                  ml: 1,
                  "& svg path": {
                    fill: hasInvestigationResponsable(user) ? "#D4A017" : "#bdbdbd",
                  },
                  "&:hover svg path": {
                    fill: hasInvestigationResponsable(user) ? "#B8860B" : "#9e9e9e",
                  }
                }}
              >
                <Icon
                  name={hasInvestigationResponsable(user) ? "bold_crown_3" : "bold_crown"}
                  size={24}
                />
              </IconButton>
            </Tooltip>
          )}
        </ListItem>

        <ModalAssignUser
          user={user}
          handleChange={handleChange}
          setIsLoading={setIsItemLoading}
          show={showModal}
          target={target}
          onHide={() => setShowModal(false)}
        />
      </>
    );
  };

  // 🔹 Componente interno para cada grupo
  const GroupItem = ({ group }) => {
    const isBusy = busyGroupId === group.id;

    return (
      <li className="group-item dyBorder1 rounded p-3">
        <div className="d-flex align-items-center gap-2 mb-2">
          <Icon name="bold_people" color={group.assigned ? "#02665c" : "#5c666e"} size={22} />
          <div className="flex-grow-1">
            <div className="d-flex align-items-center gap-2 flex-wrap">
              <strong>{group.name}</strong>
              {group.assigned && (
                <span className="group-badge">{t("Groups.assigned_badge")}</span>
              )}
            </div>
            <small className="text-muted">
              {t("Groups.members_count", { count: group.users.length })}
            </small>
          </div>
        </div>

        <div className="d-flex justify-content-end gap-2">
          {group.assigned ? (
            <>
              <MCTooltip text={t("Groups.resync_hint")} position="top">
                <MCButton
                  label={t("Groups.resync")}
                  variant="outline-primary"
                  size="sm"
                  disabled={isBusy}
                  onClick={() => handleGroupAction(group, "assign")}
                />
              </MCTooltip>
              <MCButton
                label={t("Groups.unassign")}
                variant="outline-danger"
                size="sm"
                disabled={isBusy}
                onClick={() => handleGroupAction(group, "unassign")}
              />
            </>
          ) : (
            <MCButton
              label={t("Groups.assign")}
              variant="primary"
              size="sm"
              disabled={isBusy}
              onClick={() => handleGroupAction(group, "assign")}
            />
          )}
        </div>
      </li>
    );
  };

  return (
    <>
      <div className="my-3 p-4 dyTheme1 dyBorder1 rounded d-flex flex-column flex-md-row justify-content-between align-items-center">
        <h4 className="text-center mb-0">{t("Users.manage_users")}</h4>
      </div>

      <section className="my-3">
        <Row>
          {/* Usuarios inactivos */}
          <Col lg="6" className="p-0 pe-lg-2">
            <div className="dyTheme1 dyBorder1 rounded p-4 inactive-users">
              <div className="d-flex gap-2 mb-2">
                <Icon name="bold_user_minus" color={"purple"} />
                <h5>{t("Users.unassign_users")}</h5>
              </div>
              <div className="d-flex align-items-center">
                <SearchBar
                  placeholder={t("Users.enter_user")}
                  value={search}
                  onChange={(e) => handleSearch(e.target.value)}
                />
                <MCButton
                  variant="primary"
                  icon="outline_profile_add"
                  iconSize={24}
                  className="py-0 px-2"
                  size="sm"
                  onClick={() => setShowModalAddExternalUser(true)}
                />
              </div>
              <hr />
              <ul className="p-0" style={{ listStyle: "none" }}>
                {filteredInactiveUsers.length === 0 && search && <h5>{t("Users.no_results")}</h5>}
                {(filteredInactiveUsers.length > 0
                  ? filteredInactiveUsers
                  : showAllInactive ? inactiveUsers : inactiveUsers.slice(0, 20)
                ).map((user) => (
                  <UserListItem key={user.id} user={user} target="inactive" />
                ))}
              </ul>
              {!search && inactiveUsers.length > 20 && (
                <div className="d-flex justify-content-center mt-2">
                  <MCButton
                    label={showAllInactive ? t("Users.show_less") : t("Users.show_all")}
                    variant="outline-primary"
                    size="sm"
                    onClick={() => setShowAllInactive(!showAllInactive)}
                  />
                </div>
              )}
            </div>
          </Col>

          {/* Usuarios activos */}
          <Col lg="6" className="p-0 pe-lg-2">
            <div className="dyTheme1 dyBorder1 rounded p-4 active-users">
                <div className="d-flex gap-2 mb-2">
                  <Icon name="bold_user_tick" color={"green"} />
                  <h5>
                    {`${t("Users.assigned_user")}${activeUsers.length === 0 ? ":" : ` (${activeUsers.length})`}`}
                  </h5>
                </div>

                  <hr />
                  <div>
              {!isLoading ? (
                  <ul className="p-0" style={{ listStyle: "none" }}>
                    {[...activeUsers]
                      .sort((a, b) => {
                        const aIsResponsable = hasInvestigationResponsable(a);
                        const bIsResponsable = hasInvestigationResponsable(b);
                        if (aIsResponsable && !bIsResponsable) return -1;
                        if (!aIsResponsable && bIsResponsable) return 1;
                        return 0;
                      })
                      .map((user) => (
                        <UserListItem key={user.id} user={user} target="active" />
                      ))}
                  </ul>
              ) : (
                <h5 className="mb-0">Cargando usuarios...</h5>
              )}
            </div>
            </div>
          </Col>
        </Row>

        {/* Asignación por grupos */}
        <Row className="p-0 mt-3">
          <div className="dyTheme1 dyBorder1 rounded p-4 group-assign">
            <div
              className="d-flex gap-2 align-items-center group-toggle"
              onClick={() => setShowGroups((prev) => !prev)}
            >
              <Icon name="bold_people" color={"green"} />
              <h5 className="mb-0 flex-grow-1">
                {`${t("Groups.assign_by_group")}${
                  !groupsLoading && groups.length > 0 ? ` (${groups.length})` : ""
                }`}
              </h5>
              <Icon name={showGroups ? "bold_arrow_up_2" : "bold_arrow_down"} size={20} />
            </div>
            {showGroups && (
              <>
                <hr />
                {groupsLoading ? (
                  <h5 className="mb-0">{t("Groups.loading")}</h5>
                ) : groups.length === 0 ? (
                  <h5 className="mb-0">{t("Groups.no_active_groups")}</h5>
                ) : (
                  <ul className="p-0 group-list">
                    {groups.map((group) => (
                      <GroupItem key={group.id} group={group} />
                    ))}
                  </ul>
                )}
              </>
            )}
          </div>
        </Row>

        {/* Usuarios bloqueados */}
        {blockedUsers.length > 0 && (
          <Row className="p-0 ps-lg-2 mt-3 mt-lg-0">
            <div className="dyTheme1 dyBorder1 rounded p-4 mt-3 blocked-users">
              <div className="d-flex gap-2 mb-2">
                <Icon name="bold_forbidden_2" color={"red"} />
                <h5>{t("Users.blocked_users")}</h5>
              </div>
              <hr />
              <ul className="d-flex flex-row flex-wrap gap-2 p-0">
                {blockedUsers.map((user) => (
                  <UserListItem key={user.id} user={user} target="blocked" />
                ))}
              </ul>
            </div>
          </Row>
        )}
      </section>

      {/* Modal de confirmación para asignar responsable */}
      <Confirmation
        show={showConfirmResponsable}
        onHide={() => {
          setShowConfirmResponsable(false);
          setPendingResponsableUser(null);
        }}
        variant="warning"
        title={t("User.confirm_responsable_title")}
        titleColor={theme.palette.pink.main}
        description1={t("User.confirm_responsable_message", { name: pendingResponsableUser?.name })}
        description2=""
        question={t("User.confirm_responsable_question")}
        leftButtonText={t("User.cancel")}
        onClickLeft={() => {
          setShowConfirmResponsable(false);
          setPendingResponsableUser(null);
        }}
        rightButtonText={t("User.confirm")}
        onClickRight={handleConfirmResponsable}
        ethiquin="progress"
        hideEthiquin
      />
    </>
  );
};
